import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import CountryCodeModal from "../CountryCodeModal/CountryCodeModal";
import Swal from "sweetalert2";

const Footer = ({ contactRef }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+1",  
    phone: "",
    message: "",
  });

  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showScroll, setShowScroll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasSelectedCountryCode, setHasSelectedCountryCode] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "phone") {
      const fullPhone = `${formData.countryCode}${value}`;
      const phonePattern = /^\+\d{1,4}\s?\d{4,14}$/;
      if (value && !phonePattern.test(fullPhone)) {
        setPhoneError("Enter a valid phone number (e.g. +1 1234567890)");
      } else {
        setPhoneError("");
      }
    }

    if (name === "countryCode") {
      const fullPhone = `${value}${formData.phone}`;
      const phonePattern = /^\+\d{1,4}\s?\d{4,14}$/;
      if (formData.phone && !phonePattern.test(fullPhone)) {
        setPhoneError("Enter a valid phone number (e.g. +1 1234567890)");
      } else {
        setPhoneError("");
      }
    }

    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailPattern.test(value)) {
        setEmailError("Enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (phoneError) {
      Swal.fire({
        icon: "error",
        title: "Invalid Phone Number",
        text: phoneError,
        confirmButtonColor: "#f39c12",
      });
      return;
    }

    if (emailError) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email Address",
        text: emailError,
        confirmButtonColor: "#f39c12",
      });
      return;
    }

    const serviceID = "service_cqtaivh";
    const templateID = "template_9urj6bs";
    const userID = "Bj5MjMXmtE9aG7Ily";

    const templateParams = {
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: `${formData.countryCode} ${formData.phone}`,
      message: formData.message,
      time: new Date().toLocaleString(),
    };

    emailjs
      .send(serviceID, templateID, templateParams, userID)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: "Thank you for contacting us. We’ll get back to you shortly.",
          confirmButtonColor: "#f39c12",
        });

        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          countryCode: "+1",  
          phone: "",
          message: "",
        });

        setHasSelectedCountryCode(false);  
        setPhoneError("");
        setEmailError("");
      })
      .catch((error) => {
        console.error("Error sending message:", error);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong. Please try again later.",
          confirmButtonColor: "#f39c12",
        });
      });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <footer className="footer">
        <div className="contact-us" ref={contactRef}>
          <h2>Contact Us</h2>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label>First Name*</label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Last Name*</label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email*</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={emailError ? "invalid" : ""}
                />
                {emailError && (
                  <small className="error-message">{emailError}</small>
                )}
              </div>

              <div className="form-group">
                <label>Phone</label>
                <div className="phone-input-group">
                  <button
                    type="button"
                    className="country-code-button"
                    onClick={() => setIsModalOpen(true)}
                  >
                    {hasSelectedCountryCode ? (
                      formData.countryCode  
                    ) : (
                      <i className="fas fa-globe"></i> 
                    )}
                  </button>

                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={phoneError ? "invalid" : ""}
                  />
                </div>
                {phoneError && (
                  <small className="error-message">{phoneError}</small>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>Write a Message*</label>
              <textarea
                name="message"
                placeholder="Give a detailed message"
                value={formData.message}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="btn-container">
              <button type="submit" className="submit-button">
                Submit
              </button>
            </div>
          </form>
        </div>

        {showScroll && (
          <button
            className="scroll-to-top"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            ↑
          </button>
        )}
      </footer>

      <div className="mini-footer">
        <div className="footer-info">
          <a href="tel:+1(647)708-6212" className="footer-link">
            <i className="fas fa-phone-alt mr-2"></i> +1(647)708-6212
          </a>
          <a
            href="mailto:info@portoconsulting.ca"
            className="footer-link"
          >
            <i className="fas fa-envelope mr-2"></i> info@portoconsulting.ca
          </a>
        </div>
      </div>

      <CountryCodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={(code) => {
          setFormData((prev) => ({ ...prev, countryCode: code }));
          setHasSelectedCountryCode(true);  
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default Footer;
